import { cx } from '@linaria/core';
import { useId, useRef, useState, useMemo, useEffect } from 'react';
import { floatingButtonSelectors, pm2PlaceholderSelectors } from '@pm2/ui-layout/selectors';
import { useNotifier } from '@provider/notifier';
import { resets, breakpoints } from '@ui/css';
import { Overlay, FocusOn } from '@ui/kit';
import { xml2ast, allHtmlParsers, printHtmlNodesToJsx } from '@utils/ast';
import { throttleToAnimationFrame, preloadImage } from '@utils/common';
import { convertColorStringToRgb, invertColorByWCAGContrastRatio } from '@utils/css';
import { useIsomorphicLayoutEffect, PostloadedImage, useMatchBreakpoint } from '@utils/react';
import { imageScale, hotspotSurface, aspectRatio, imageWrapper, imageContainer, image, hotspotList, hotspotGraphic, hotspotBullet, hotspotModifiers, trigger, hotspot } from './_content.style.js';
import { HotspotContent } from './molecule.hotspot-content.js';
import { MobileButtons } from './molecule.mobile-buttons.js';
import { jsxs, jsx } from 'react/jsx-runtime';

function ImageHotspotsContent(props) {
  const id = useId();
  const ref = useRef(null);
  const [scale, setScale] = useState(undefined);
  const longDescAst = useMemo(() => xml2ast(props.component.data.background?.longDesc ?? "", allHtmlParsers, "text/html"), [props.component.data.background?.longDesc]);
  const colorOverrides = useMemo(() => {
    const backgroundColor = convertColorStringToRgb(props.component.data.settings.hotspotColor);
    if (!backgroundColor) {
      return undefined;
    }
    return {
      backgroundColor: backgroundColor.toString(),
      textColor: invertColorByWCAGContrastRatio(backgroundColor.toString(), {
        preferredColor: "#fff",
        invertedColor: "#000"
      })
    };
  }, [props.component.data.settings.hotspotColor]);
  useIsomorphicLayoutEffect(() => {
    const handleResize = () => {
      if (!ref.current || !props.component.data.background?.dimensions.naturalWidth) {
        setScale(undefined);
        return;
      }
      setScale(ref.current.clientWidth / props.component.data.background?.dimensions.naturalWidth);
    };
    handleResize();

    // observe for changes in size of the element
    const observer = new ResizeObserver(throttleToAnimationFrame(handleResize));
    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => {
      observer.disconnect();
    };
  }, [props.component.data.background?.dimensions.naturalWidth]);
  return /*#__PURE__*/jsxs("div", {
    style: {
      ...hotspotSurface.__override({
        background: colorOverrides?.backgroundColor,
        color: colorOverrides?.textColor
      }),
      ...imageScale.__override(scale)
    },
    children: [/*#__PURE__*/jsx("div", {
      ref: ref,
      className: aspectRatio,
      style: {
        aspectRatio: `${props.component.data.background?.dimensions.naturalWidth ?? 16} / ${props.component.data.background?.dimensions.naturalHeight ?? 9}`,
        maxWidth: `${props.component.data.background?.dimensions.naturalWidth ?? 1110}px`
      },
      children: /*#__PURE__*/jsxs("div", {
        className: imageWrapper,
        children: [/*#__PURE__*/jsxs("div", {
          className: imageContainer,
          children: [/*#__PURE__*/jsx(PostloadedImage, {
            className: image,
            src: props.component.data.background?.src,
            alt: props.component.data.background?.alt ?? "",
            "aria-details": longDescAst.length ? `transcript-${id}` : undefined
          }), !!longDescAst.length && /*#__PURE__*/jsx("div", {
            className: resets.screenReaderOnlyCls,
            "data-disable-polly": true,
            id: `transcript-${id}`,
            children: printHtmlNodesToJsx(longDescAst)
          })]
        }), /*#__PURE__*/jsx("ol", {
          className: hotspotList,
          children: props.component.data.hotspots.map((it, index) => /*#__PURE__*/jsx(HotspotTrigger, {
            showIndex: !!props.component.data.settings.showIndexOnHotspots,
            showMode: props.component.data.settings.showMode,
            hotspot: it,
            index: index
          }, it.id))
        })]
      })
    }), /*#__PURE__*/jsx(MobileButtons, {
      component: props.component
    })]
  });
}
function HotspotTrigger(props) {
  const id = `hotspot--${useId()}`;
  const ref = useRef(null);
  const notifier = useNotifier();
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const [isExpanded, setExpanded] = useState(false);
  const [hasBeenExpanded, setHasBeenExpanded] = useState(false);
  const speechSynthesisButton = document.querySelector(floatingButtonSelectors.speechSynthesis());
  const handleClick = () => {
    setHasBeenExpanded(true);
    if (props.showMode === "modal" || isMobile) {
      notifier.modal({
        closeButtonLabel: "Luk hotspot",
        shareBackdrop: false,
        content: /*#__PURE__*/jsx(HotspotContent, {
          id: undefined,
          onClose: undefined,
          hotspot: props.hotspot
        })
      });
      return;
    }
    setExpanded(prevValue => !prevValue);
  };
  const handleClose = () => {
    setExpanded(false);
  };
  useEffect(() => {
    if (!props.hotspot.media.photo?.src) {
      return;
    }
    preloadImage(props.hotspot.media.photo.src);
  }, [props.hotspot.media.photo?.src]);
  return /*#__PURE__*/jsxs("li", {
    className: cx(hotspot, isExpanded && hotspotModifiers.active.className, hasBeenExpanded && hotspotModifiers.withoutPulse.className, props.hotspot.hotspotGraphicsURL && hotspotModifiers.withGraphic.className),
    style: {
      top: `${props.hotspot.position.y * 100}%`,
      left: `${props.hotspot.position.x * 100}%`
    },
    children: [/*#__PURE__*/jsxs("button", {
      ref: ref,
      className: cx(trigger),
      "aria-label": props.hotspot.title ? `Åbn "${props.hotspot.title}"` : "Åbn hotspot",
      "aria-expanded": isExpanded,
      "aria-controls": id,
      onClick: handleClick
      // prevent FocusOn onClickOutside from running when clicking this button
      // to ensure that the onClick toggle handler triggers the expected
      // outcome of toggling the button on/off
      ,
      onMouseDown: evt => {
        if (isExpanded) {
          evt.stopPropagation();
        }
      },
      onTouchStart: evt => {
        if (isExpanded) {
          evt.stopPropagation();
        }
      },
      children: [props.hotspot.hotspotGraphicsURL &&
      /*#__PURE__*/
      // eslint-disable-next-line @next/next/no-img-element
      jsx("img", {
        className: hotspotGraphic,
        src: props.hotspot.hotspotGraphicsURL,
        alt: props.hotspot.hotspotGraphicsAlt ?? "",
        role: "presentation"
      }), /*#__PURE__*/jsx("span", {
        className: cx(hotspotBullet, props.showIndex && hotspotModifiers.withIndex.className),
        children: props.index + 1
      })]
    }), /*#__PURE__*/jsx(Overlay, {
      mountIn: document.querySelector(pm2PlaceholderSelectors.postPageRouter()) ?? document.body,
      active: isExpanded && !isMobile,
      placement: "bottom",
      containerRef: ref,
      withArrow: true,
      offsetPx: -8,
      marginPx: 60,
      children: /*#__PURE__*/jsx(FocusOn, {
        onEscapeKey: handleClose,
        onClickOutside: handleClose,
        scrollLock: false,
        focusLock: true,
        autoFocus: true,
        noIsolation: true,
        allowPinchZoom: true,
        returnFocus: {
          preventScroll: true
        },
        preventScrollOnFocus: true,
        shards: speechSynthesisButton ? [speechSynthesisButton] : undefined,
        children: /*#__PURE__*/jsx(HotspotContent, {
          id: id,
          hotspot: props.hotspot,
          onClose: handleClose
        })
      })
    })]
  });
}

export { ImageHotspotsContent };
//# sourceMappingURL=_content.js.map
